import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { fetchJSON, putJSON } from 'api/fetch';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';

import { selectDateWages } from 'features/scheduleBuilder/slices/roleLaborTotals/slice';

import { selectCurrentDateRangeStart } from '../../ScheduleBuilderView/selectors/index';
import { selectTotalWagesForAllDepartments } from '../departments/slice';
import {
  selectEstimatedSalesValueByDate,
  selectSummarySales,
} from '../estimatedSales/slice';

import { SLICE_NAME } from './constants';
import {
  LaborTarget,
  LaborTargetPayloadItem,
  LaborTargetsParams,
  LaborTargetsPayload,
  LaborTargetsState,
  UpdateLaborTargetParams,
  UpdateLaborTargetPayload,
} from './types';

export const fetchLaborTargets = createAsyncThunk<
  LaborTargetsPayload,
  LaborTargetsParams
>(`${SLICE_NAME}/fetch`, async (params: LaborTargetsParams) => {
  const resp = await fetchJSON(
    `/schedule_builder/labor_targets?start_date=${params.startDate}&end_date=${params.endDate}`
  );

  return resp;
});

export const updateLaborTarget = createAsyncThunk<
  UpdateLaborTargetPayload,
  UpdateLaborTargetParams
>(`${SLICE_NAME}/update`, async ({ date, value, weekly }, { getState }) => {
  const state = getState() as RootState;
  const startDate = selectCurrentDateRangeStart(state);
  const summaryId = `summary_${startDate}`;

  const resp = await putJSON(
    `/schedule_builder/labor_targets/${weekly ? summaryId : date}`,
    {
      data: {
        type: 'laborTarget',
        id: weekly ? summaryId : date,
        attributes: {
          value,
          weekly,
        },
      },
    }
  );

  // TODO: https://joinhomebase.atlassian.net/browse/SB-5344
  // This does not currently work, need to figure, backend
  // wrong data so we never have weeky labor targets.

  // if (!weekly) {
  //   // Remove weekly value if day value has been changed
  //   await dispatch(deleteLaborTarget({ id: summaryId }));
  // }

  return resp;
});

const laborTargetsAdapter = createEntityAdapter<LaborTarget>();

export const initialState: LaborTargetsState =
  laborTargetsAdapter.getInitialState({
    status: 'idle',
    error: undefined,
  });

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    removeAll: state => {
      laborTargetsAdapter.removeAll(state);
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchLaborTargets.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(fetchLaborTargets.fulfilled, (state, action) => {
      const items = action.payload.data.map((item: LaborTargetPayloadItem) => ({
        id: item.id,
        type: item.type,
        value: item.attributes.value,
        weekly: item.attributes.weekly,
      }));

      laborTargetsAdapter.setAll(state, items);
      state.status = 'succeeded';
    });
    builder.addCase(fetchLaborTargets.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch labor targets';
    });
    builder.addCase(updateLaborTarget.pending, state => {
      state.status = 'pending';
    });
    builder.addCase(updateLaborTarget.fulfilled, (state, action) => {
      const { id, type, attributes } = action.payload?.data;
      laborTargetsAdapter.upsertOne(state, {
        id,
        type,
        value: attributes.value,
        weekly: attributes.weekly,
      });
    });
    builder.addCase(updateLaborTarget.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to update labor target';
    });
  },
});

export const { reducer, actions } = slice;

const getScheduleBuilderState = (state: RootState) =>
  state.get('scheduleBuilderSchedule');

export const {
  selectById: selectLaborTargetsById,
  selectIds: selectLaborTargetsIds,
  selectEntities: selectLaborTargetsEntities,
  selectAll: selectAllLaborTargets,
  selectTotal: selectTotalLaborTargets,
} = laborTargetsAdapter.getSelectors<RootState>(
  state => getScheduleBuilderState(state)[SLICE_NAME] || initialState
);

export const selectLaborTargetsStatus = (state: RootState) => {
  const scheduleBuilderState = getScheduleBuilderState(state);
  return scheduleBuilderState[SLICE_NAME]?.status || 'idle';
};

export const selectLaborTargetsFetchIsPending = (state: RootState): boolean =>
  selectLaborTargetsStatus(state) === 'pending';

export const selectLaborTargetAttribute = <T extends keyof LaborTarget>({
  state,
  attribute,
  id,
}: {
  state: RootState;
  attribute: T;
  id: string;
}): LaborTarget[T] | undefined => {
  const laborTarget = selectLaborTargetsById(state, id);
  return laborTarget ? laborTarget[attribute] : undefined;
};

export const selectLaborTargetsByAttribute = <T extends keyof LaborTarget>({
  state,
  attribute,
  value,
}: {
  state: RootState;
  attribute: T;
  value: LaborTarget[T];
}): LaborTarget[] => {
  const allLaborTargets = selectAllLaborTargets(state);
  return allLaborTargets.filter(
    laborTarget => laborTarget[attribute] === value
  );
};

function getPercentage(wages?: number, target?: number): number | null {
  return wages && target ? (wages / target) * 100 : null;
}

const selectWeekSummary = createSelector(
  (state: RootState) => selectCurrentDateRangeStart(state),
  (state: RootState) =>
    selectLaborTargetsByAttribute({
      state,
      attribute: 'weekly',
      value: true,
    }),
  (startDate: string, laborTargets: LaborTarget[]) =>
    laborTargets.find(l => l.id === `summary_${startDate}`)
);

export const selectSummaryLaborTargets = createSelector(
  (state: RootState) =>
    selectLaborTargetsByAttribute({
      state,
      attribute: 'weekly',
      value: false,
    }),
  selectWeekSummary,
  (laborTargets: LaborTarget[], summary?: LaborTarget) =>
    summary
      ? summary.value
      : laborTargets.reduce((memo, target) => memo + target.value, 0) / 7.0
);

export const selectSummaryActual = createSelector(
  selectTotalWagesForAllDepartments,
  selectSummarySales,
  getPercentage
);

export const selectActualLaborForDate = createCachedSelector(
  // eslint-disable-next-line
  // @ts-ignore need to ignore until we type selectDateWages
  // inference is broken
  (state: RootState, props: { date: string }) => selectDateWages(state, props),
  (state: RootState, props: { date: string }) =>
    selectEstimatedSalesValueByDate(state, props.date),
  getPercentage
)((_, props) => `selectLaborTargetForDate-${props.date}`);
